// ---------------------------------------------------------------------------
// routeReason — the single clean sentence that answers "why this route?"
//
// Design (agreed with Morgan): each card leads with ONE plain-language
// reason + the time trade. No pill clutter. The reason is generated from a
// four-rung ladder, most-specific to most-generic, that NEVER falls off the
// bottom — because road-character percentages (residential/highway) are
// computed for every route from its own geometry and are always present:
//
//   1. Named feature   → "Along the Pocantico River, past Patriots Park"
//   2. Counted features→ "Past two parks and a stretch of river"
//   3. Road character  → "Quiet residential streets nearly the whole way"
//   4. Pure comparison → "Calmer streets, about the same time"  (the floor)
//
// And when a route is genuinely no different from the direct one, we SAY so
// ("The most direct way — nothing calmer on this trip"). That honesty is the
// brand, not a failure mode.
// ---------------------------------------------------------------------------

import type { ClassifiedRoute, FeatureSummary } from "@/utils/scoring";
import { MOSTLY_MAIN_ROADS_HWY_PCT } from "@/utils/scoring";
import { curatedRoadsTravelled, displayRoadName } from "@/utils/scenicRoads";
import { statesAlongRoute, countriesAlongRoute, displayCountry } from "@/utils/regionsAlongRoute";
import { routeDivergence, describeDivergencePosition, roadsDuringDivergence } from "@/utils/routeDivergence";
import { niceTownsAlongRoute } from "@/utils/niceTowns";
import { haversine, type LngLat } from "@/utils/polyline";
import { greenPhrase, namedPhrase, sceneryClause } from "@/utils/greenPhrase";
import { cleanRoadNameList, sanitizeRoadName } from "@/utils/roadNames";

// ---------------------------------------------------------------------------
// SIGNATURE CORRIDORS — hand-written reasons for a few flagship drives where the
// generic builder undersells the route. Matched on the trip's actual endpoints
// (start + destination of THIS route's polyline). Only used for the pleasant /
// scenic card, never the plain Fast card. Add entries as we curate more hero
// routes. (Morgan: "custom write an amazing why this route for Boston→Ellsworth".)
// ---------------------------------------------------------------------------
interface SignatureRoute {
  start: LngLat;
  end: LngLat;
  /** Match radius around each endpoint, km. Generous so any Boston-metro →
   *  Ellsworth pairing matches, not one exact lat/long. */
  radiusKm: number;
  headline: string;
  comparison: string;
  blurb: string;
}
const SIGNATURE_ROUTES: SignatureRoute[] = [
  {
    // Boston, MA → Ellsworth, ME — the coastal US-1 run up the Maine shore.
    start: [-71.0589, 42.3601],
    end: [-68.4194, 44.5437],
    radiusKm: 40,
    headline: "Up the Maine coast on US-1",
    comparison: "Far more coastline — and fewer interstate miles — than the fast route",
    blurb:
      "The slow way north: Massachusetts into Maine along the shore, past Camden's harbor and Penobscot Bay, through the lobster towns and white-clapboard villages that I-95 leaves inland and behind. One of New England's great drives — take your time.",
  },
  {
    // Briarcliff Manor, NY → Greenwich, CT — the Westchester back-road run.
    start: [-73.8235, 41.1426],
    end: [-73.6285, 41.0262],
    radiusKm: 12,
    headline: "Back roads into Greenwich's backcountry",
    comparison: "Country lanes nearly the whole way instead of the parkways and I-95",
    blurb:
      "Trades the Hutch and I-95 for horse-country back roads through Pocantico Hills and northern Westchester, slipping across the line into Greenwich on Riversville Road — stone walls, reservoirs, and barely a traffic light. A little longer, a lot nicer.",
  },
];

/** Confessions already printed (rule 17) — the card re-renders constantly, so
 *  each unique stand-down verdict is logged once, not once per paint. */
const signatureStandDownsLogged = new Set<string>();

/** A hand-written reason when THIS route's endpoints match a signature corridor.
 *  Returns null for the Fast card or any non-matching trip.
 *
 *  AT MOST ONE CARD PER TRIP (Morgan, Aug 20: "the pleasant and scenic copy is
 *  the same" — 54 min and 37 min routes wearing identical hand-written copy).
 *  The endpoints match every non-fast card on the trip, but the copy describes
 *  ONE route — the committed, longer back-roads run ("a little longer, a lot
 *  nicer"). So the SLOWEST matching non-fast card carries it; any faster
 *  sibling stands down to the generated ladder, which describes what that
 *  route actually is. */
function signatureReason(
  route: ClassifiedRoute,
  isFastest: boolean,
  peers: PeerRoute[] = [],
): RouteReason | null {
  if (isFastest) return null;
  const coords = route.route.coordinates ?? [];
  if (coords.length < 2) return null;
  const a = coords[0];
  const b = coords[coords.length - 1];
  for (const s of SIGNATURE_ROUTES) {
    const rM = s.radiusKm * 1000;
    if (haversine(a, s.start) <= rM && haversine(b, s.end) <= rM) {
      // A non-fast peer on the same trip matches this corridor by definition
      // (same endpoints). If one is slower — or same time but longer — IT is
      // the route the copy was written about; this card tells its own story.
      const dur = route.route.duration ?? 0;
      const dist = route.route.distance ?? 0;
      const slowerPeer = peers.find(
        (p) =>
          p.classification != null &&
          p.classification !== "fastest" &&
          (p.durationS > dur || (p.durationS === dur && p.distanceM > dist)),
      );
      if (slowerPeer) {
        const key = `${s.headline}|${route.classification}|${Math.round(dur)}`;
        if (!signatureStandDownsLogged.has(key)) {
          signatureStandDownsLogged.add(key);
          // eslint-disable-next-line no-console
          console.log(
            `[signature] stand-down: "${s.headline}" goes to the slower ${slowerPeer.classification} card (${Math.round(slowerPeer.durationS / 60)}m vs this ${route.classification} ${Math.round(dur / 60)}m) — using the generated reason here`,
          );
        }
        return null;
      }
      return {
        headline: s.headline,
        comparison: s.comparison,
        tradeoff: undefined as unknown as string,
        featured: { name: s.headline, blurb: s.blurb, roads: [] },
      };
    }
  }
  return null;
}

export interface RouteReason {
  /** The headline — the most specific thing worth noting about THIS route
   *  (a named river/park/highway when present, else its road character). */
  headline: string;
  /** How this route compares to the others on screen (e.g. "Less time on the
   *  highway"). Only set when it adds something the headline doesn't already
   *  say — so a card can show both "Runs along Pacific Coast Highway" AND
   *  "Less time on the highway". Undefined when there's nothing distinct. */
  comparison?: string;
  /** The time-trade line beneath it, e.g. "4 min slower" / "The quickest way". */
  tradeoff: string;
  /** A curated 'pleasant road' this route genuinely travels. The headline names
   *  it ("… featuring Round Hill Road"); `blurb` is the top road's descriptor
   *  (shown as a truncatable line), and `roads` is the full ordered list of
   *  every pleasant road on the route so the card can expand "and N more".
   *  Undefined when no pleasant road is on the route. */
  featured?: { name: string; blurb?: string; roads: string[] };
}

/** A sibling route to compare against — the OTHER routes on screen. Only the
 *  fields the comparison needs. */
export interface PeerRoute {
  features?: Partial<FeatureSummary>;
  durationS: number;
  distanceM: number;
  /** The peer's dominant road (from dominantRoadLabel). Lets a headline avoid
   *  naming a road BOTH routes share — which differentiates nothing. */
  dominantRoad?: string | null;
  /** The peer's card slot ("pleasant" | "scenic" | "fastest" | ...). Lets the
   *  signature-corridor copy go to exactly ONE card per trip. Optional so old
   *  callers keep working — an unknown peer never claims the signature. */
  classification?: string;
}

/** "Pocantico River" → "the Pocantico River"; "Swan Lake" → "Swan Lake". */
function withArticle(name: string): string {
  const t = name.trim();
  if (/^the\b/i.test(t)) return t;
  if (/\b(river|creek|brook|canal|reservoir|aqueduct|sound)$/i.test(t)) return `the ${t}`;
  return t;
}

function ordinalWord(n: number): string {
  return n === 1 ? "one" : n === 2 ? "two" : n === 3 ? "three" : n === 4 ? "four" : `${n}`;
}

/** The time-trade line. `isFastest` = this route is the lowest-duration one. */
export function buildTradeoff(
  routeDurationS: number,
  fastestDurationS: number,
  isFastest: boolean,
  /** Extra distance over the fastest route, in metres. When supplied and the
   *  detour is large, the line names the distance cost too. */
  extraDistanceM?: number,
): string {
  const deltaMin = Math.round((routeDurationS - fastestDurationS) / 60);
  if (isFastest || deltaMin <= 0 && deltaMin >= -1) {
    return isFastest ? "The quickest way" : "About the same time";
  }
  if (deltaMin < -1) return `${Math.abs(deltaMin)} min faster`;
  if (deltaMin <= 1) return "About the same time";

  // DELIBERATE REVERSAL (Morgan, Aug 24 — card-copy audit).
  //
  // This used to `return ""` for every slower route, on the reasoning that the
  // driver can compare the two ETAs and that naming the cost "undersells the
  // pleasant route". The audit showed what that silence actually buys: Phoenix
  // → Prescott offered +1h 51m and 79 extra miles with no time line at all,
  // and Seattle → Olympia +1h 28m. A cost the card declines to mention is not
  // a smaller cost — it is the same cost, discovered later.
  //
  // Morgan's rule now: "If Pleasant is +2 minutes, say that." Small trades are
  // cheap to state and make the card an easy yes; large ones must be explicit
  // precisely BECAUSE they are hard to justify.
  if (deltaMin <= 10) return `+${deltaMin} min`;

  const h = Math.floor(deltaMin / 60);
  const m = deltaMin % 60;
  const time = h > 0 ? (m > 0 ? `+${h}h ${m}m` : `+${h}h`) : `+${deltaMin} min`;
  const extraMi = extraDistanceM != null ? extraDistanceM / 1609.344 : null;
  // Only name the distance when it is a real part of the story (≥ 1 mi).
  if (extraMi != null && extraMi >= 1) {
    return `${time} · ${extraMi < 10 ? extraMi.toFixed(1) : Math.round(extraMi)} mi longer`;
  }
  return time;
}

/**
 * WAS SCENERY ACTUALLY MEASURED ON THIS ROUTE? (card-copy audit, Aug 24)
 *
 * Rule 20 in the display layer. On a search whose pass-1 enrichment never
 * became authoritative, the scenery fields are not zero — they are ABSENT.
 * Every one of `parkNames`, `waterNames`, `natureNames`, `historicNames`,
 * `treeLinedKm` and `scenicBywayKm` comes back `undefined`, which is exactly
 * what `?? 0` and `?.[0]` quietly turn into "no parks here".
 *
 * The distinction matters most on the Scenic card, whose entire job is to say
 * why the detour is worth taking. With nothing measured there is no answer, so
 * the card must say so rather than reach for a generic road-character line —
 * which is how Phoenix → Prescott came to offer "+1h 51m" of extra driving
 * under the headline "A mix of local and main roads".
 *
 * Presence of ANY scenery key means the enrichment pass wrote something. An
 * empty array is a real measurement of zero and counts as measured; undefined
 * does not.
 *
 * TWO SOURCES, NOT ONE (Morgan, Aug 31: Dallas -> Austin, "Scenery not
 * measured"). Until now this asked only the Overpass FeatureSummary, so it
 * answered "no" whenever the live tile path failed — which is most of the
 * time: 84% of searches over the last three days recorded no enrichment, and
 * outside the nine states the private box holds there is no live source at all
 * once the public mirrors are down. Meanwhile the IMPORTED layer covers 44
 * regions. That Dallas -> Austin corridor came back `coveredFraction: 1.0`
 * with 1,061 features and 26 nameable places, in 11 ms, while the card told
 * the driver we had not looked.
 *
 * The layer is a measurement. It is the better one — extract-shaped coverage,
 * a real UNMEASURED state, and no 20-second budget. Ask it first.
 *
 * THE BAR IS coveredFraction >= 0.5, matching minutesRule.ts, which already
 * refuses to judge a route whose scenery is mostly unimported. Anything above
 * zero would be too loose: a corridor half in Canada sums to a confident-
 * looking fraction while a third of the drive has no data behind it.
 * `undefined` means the lookup never ran and `0` means it ran and found the
 * route outside every region — both fall through to the Overpass answer
 * rather than counting as measurement (scoring.ts says this explicitly:
 * anything reading scenicLayer must branch on coveredFraction or it rebuilds
 * rule 20's defect in the copy).
 */
/** Share of a route that must sit inside imported coverage before the layer
 *  counts as having measured it. Same bar as minutesRule.ts. */
export const SCENERY_COVERED_MIN = 0.5;

export function sceneryMeasured(route: ClassifiedRoute): boolean {
  const f = route.features as Partial<FeatureSummary> | undefined;
  if (!f) return false;
  const layer = f.scenicLayer;
  if (layer && (layer.coveredFraction ?? 0) >= SCENERY_COVERED_MIN) return true;
  return (
    f.parkNames !== undefined ||
    f.waterNames !== undefined ||
    f.natureNames !== undefined ||
    f.historicNames !== undefined ||
    f.treeLinedKm !== undefined ||
    (f as { scenicBywayKm?: number }).scenicBywayKm !== undefined
  );
}

/** The single road this route spends most distance on, sanitized. Unlike
 *  dominantRoadLabel this has no share threshold — it is the best anchor
 *  available when no road dominates. */
function longestRoadName(route: ClassifiedRoute): string | null {
  const steps = (route.route.steps ?? []) as Array<{ roadName?: string; distance?: number }>;
  const byRoad = new Map<string, number>();
  for (const s of steps) {
    const name = (s.roadName ?? "").trim();
    if (!name) continue;
    byRoad.set(name, (byRoad.get(name) ?? 0) + (s.distance ?? 0));
  }
  let best: string | null = null;
  let bestM = 0;
  for (const [name, m] of byRoad) if (m > bestM) { best = name; bestM = m; }
  return best ? sanitizeRoadName(best) || null : null;
}

/** percent helper that tolerates a missing FeatureSummary. */
const pct = (f: Partial<FeatureSummary> | undefined, key: keyof FeatureSummary): number =>
  (f?.[key] as number | undefined) ?? 0;

/**
 * The comparative rung — what makes THIS route different from the others on
 * screen. This is the heart of the card: two plain routes should never read
 * the same, because each one stands out on *some* axis relative to its peers.
 *
 * We compare this route's road character (residential / highway / main-road /
 * tree-lined) against the mean of its peers and lead with the single biggest
 * meaningful gap. If road character is near-identical, we fall back to the
 * route's *shape* (distance) so the cards still differ. Returns null only when
 * the routes are genuinely indistinguishable, letting the absolute floor speak.
 */
function comparativeDifference(
  route: ClassifiedRoute,
  peers: PeerRoute[],
): string | null {
  if (!peers.length) return null;
  // THE DIRECT CARD IS THE BASELINE, SO IT CANNOT BE A CONTRAST WITH IT
  // (card-copy audit, Aug 24). Every line this function returns ends in `vs`
  // — " than the fast route" / " than the other routes". On the Fast card
  // that is a comparison to itself, and it shipped: NYC → New Haven and
  // Paris → Fontainebleau both rendered "More back roads than the fast
  // route" ON THE FAST CARD, in Paris on the 58-minute option against 1h36m
  // and 1h38m alternatives.
  //
  // This is a BINDING fault, not a wording one: the sentence was true of
  // some route on screen, just not the one it was printed on. buildHeadline
  // consults this comparative rung (rung 2) BEFORE the `isDirect` framing
  // (rung 3), so any positive differentiator the direct route happened to
  // hold against the pool mean won the headline outright.
  //
  // The direct card's identity is its speed. It keeps rung 1 (a named
  // feature it genuinely runs along is still true and useful) and falls
  // through to its own "Fastest — …" framing. It never borrows a peer
  // contrast.
  if (route.classification === "fastest") return null;
  const f: Partial<FeatureSummary> = route.features ?? {};
  const mean = (key: keyof FeatureSummary) =>
    peers.reduce((s, p) => s + pct(p.features, key), 0) / peers.length;
  const meanTree =
    peers.reduce((s, p) => s + (p.features?.treeLinedKm ?? 0), 0) / peers.length;

  const parkCount = (ff?: Partial<FeatureSummary>) =>
    (ff?.parkNames?.length ?? 0) + (ff?.natureNames?.length ?? 0);
  const meanParks =
    peers.reduce((s, p) => s + parkCount(p.features), 0) / peers.length;

  // (Morgan, verbatim: "say pleasant and fastest".) When the Pleasant card is
  // ITSELF the fastest option on screen (the Taconic case — the parkway is
  // both the quickest and the nicest, so no separate Fast card exists),
  // comparing it to "the fast route" compares it to itself. The card's whole
  // story is the double win — say exactly that.
  // STRICTLY fastest only: at a time TIE the comparative lines below are
  // accurate and more informative (the "same time — a nicer way" chip
  // already carries the time story). This fires when nothing faster is on
  // screen at all — so "the fast route" would be a comparison to itself.
  if (
    route.classification === "pleasant" &&
    peers.every((p) => route.route.duration < p.durationS - 30)
  ) {
    return "Pleasant and fastest";
  }

  const dResid = pct(f, "residentialPct") - mean("residentialPct");
  const dHwy = pct(f, "highwayPct") - mean("highwayPct");
  const dMajor = pct(f, "majorRoadPct") - mean("majorRoadPct");
  const dTree = (f.treeLinedKm ?? 0) - meanTree;
  const dWater = pct(f, "waterPct") - mean("waterPct");
  const dParks = parkCount(f) - meanParks;

  // Only POSITIVE differentiators — what makes THIS route nicer than the
  // others. We never surface "more highway / more main roads" as a reason:
  // nobody picks a route for being busier. A route with nothing nicer to say
  // gets no comparison line — the headline + time carry it.
  //
  // Name the thing we're comparing against, so the line is an explicit
  // contrast with the OTHER route(s) on screen (Morgan: "compare how THIS
  // route is different than THAT route"), not a vague absolute claim.
  const vs = peers.length > 1 ? " than the other routes" : " than the fast route";

  // SCENIC differences come first (water, parks/greenery) — they're the
  // strongest reason a route earned its pleasant score, so they out-rank
  // road-character. Each candidate carries the minimum gap worth calling out.
  const scenic: { mag: number; min: number; txt: string }[] = [
    { mag: dWater, min: 6, txt: `More time alongside the water${vs}` },
    { mag: dParks * 14, min: 12, txt: `Passes more parks and green space${vs}` },
  ];
  const scenicBest = scenic.filter((c) => c.mag >= c.min).sort((a, b) => b.mag - a.mag)[0];
  if (scenicBest) return scenicBest.txt;

  // (Morgan: "don't have the cards say the same thing.") Scenic and Pleasant
  // both beat the pool mean on road character whenever Fast drags it down —
  // so both cards read "More back roads than the other routes." The road-
  // character comparatives now belong to the PLEASANT card; a Scenic card
  // sticks to scenic lines (water/parks above, trees here) or falls to its
  // honest absolute floor. Deterministic — no two cards can pick the same
  // character line.
  const isScenicCard = route.classification === "scenic";
  const character: { mag: number; min: number; txt: string }[] = isScenicCard
    ? [{ mag: dTree * 8, min: 5, txt: `More tree-lined streets${vs}` }]
    : [
        { mag: dTree * 8, min: 5, txt: `More tree-lined streets${vs}` },
        { mag: dResid, min: 5, txt: `More quiet residential streets${vs}` },
        { mag: -dHwy, min: 5, txt: `More back roads${vs}` },
        { mag: -dMajor, min: 6, txt: `More calm streets${vs}` },
      ];
  const best = character.filter((c) => c.mag >= c.min).sort((a, b) => b.mag - a.mag)[0];
  if (best) return best.txt;

  // Road character is a wash. We deliberately do NOT manufacture a comparison
  // from raw distance — "a more direct path than the others" read as "more
  // direct than the Direct route" and was wrong on slower routes. Better to
  // say nothing than something misleading; the headline + time-trade carry it.
  return null;
}

/** Tidy a Mapbox road ref ("I 684" / "US 9; NY 100") into one clean label
 *  ("I-684"). Takes the first ref when several are stacked. */
function normalizeRef(ref?: string): string | undefined {
  if (!ref) return undefined;
  const first = ref.split(/[;,/]/)[0].trim();
  if (!first) return undefined;
  return first.replace(/\s+/g, "-");
}

/**
 * The single road this route spends the most distance on, IF it's genuinely
 * dominant (≥35% of the trip). Lets two otherwise-identical-sounding highway
 * routes read "Mostly on I-684" vs "Mostly on the Taconic" — the concrete,
 * honest difference. Returns null when no single road dominates (a varied
 * surface-street route) or when step data isn't available.
 */
export function dominantRoadLabel(route: ClassifiedRoute): string | null {
  const steps = route.route.steps ?? [];
  if (steps.length === 0) return null;
  const total = route.route.distance || steps.reduce((s, st) => s + (st.distance ?? 0), 0);
  if (total <= 0) return null;
  const byRoad = new Map<string, { meters: number; label: string }>();
  for (const st of steps) {
    const label = normalizeRef(st.roadRef) ?? st.roadName?.trim();
    if (!label) continue;
    const key = label.toLowerCase();
    const cur = byRoad.get(key) ?? { meters: 0, label };
    cur.meters += st.distance ?? 0;
    byRoad.set(key, cur);
  }
  let best: { meters: number; label: string } | null = null;
  for (const v of byRoad.values()) if (!best || v.meters > best.meters) best = v;
  if (!best || best.meters < total * 0.35) return null;
  // Sanitize on the way out (audit, Aug 24): this label lands in "Mostly on
  // {road}", which is where "Mostly on Hutchinson-River-Parkway" reached the
  // NYC → New Haven card — a raw slug, rendered one line above the same road
  // written correctly as "Hutchinson River Parkway". Selection is unchanged;
  // only the rendered name is cleaned.
  return sanitizeRoadName(best.label) || null;
}

/** Infer travel mode from the route's Mapbox profile (driving by default). */
function routeMode(route: ClassifiedRoute): "driving" | "walking" | "biking" {
  const p = String((route.route as { profile?: string }).profile ?? "");
  if (/walk|foot/i.test(p)) return "walking";
  if (/cycl|bik/i.test(p)) return "biking";
  return "driving";
}

/** When this route genuinely travels a meaningful stretch of a curated great
 *  road / street, name it — the most specific, most compelling reason we have
 *  (and the app's signature). We confirm the road against the route's OWN
 *  steps so we never claim a road that's merely nearby, and we name the one
 *  travelled farthest. Returns null when no curated road is meaningfully on the
 *  route. */
function curatedRanked(route: ClassifiedRoute) {
  const mode = routeMode(route);
  // Scale "defining chunk" to the mode: walking streets are short blocks, drives
  // are long. So a West Village block can headline a walk, while a quarter-mile
  // clip can't define a 20-mile drive. Same step-confirmed signal the score uses.
  const minMeters = mode === "walking" ? 90 : mode === "biking" ? 300 : 800;
  // Fraction gate keeps a short clip from defining a trip on foot/bike. Driving
  // is exempt: a famous 1.5-mile road IS the story even on a 20-mile drive, so a
  // long trip shouldn't bury it — the step-confirmed `minMeters` floor is enough.
  const minFraction = mode === "walking" ? 0.1 : mode === "biking" ? 0.12 : 0;
  return curatedRoadsTravelled(
    { coordinates: route.route.coordinates, steps: route.route.steps, distance: route.route.distance },
    mode,
    { minMeters, minFraction },
  );
}

/** The "featuring …" clause appended to the headline. One road: "featuring X".
 *  Two: "featuring X and Y". More: name the top two, summarize the rest. */
function featuringSuffix(names: string[]): string {
  if (names.length === 1) return `featuring ${names[0]}`;
  if (names.length === 2) return `featuring ${names[0]} and ${names[1]}`;
  // Always "and more pleasant roads" — never a count. "1 more pleasant roads"
  // (when exactly 3 roads) was both wrong-numbered and grammatically off
  // (Morgan). Dropping the count reads cleanly for any number of extra roads.
  return `featuring ${names[0]}, ${names[1]} and more pleasant roads`;
}

/** The standout sentence for a route. When `peers` are supplied, the card
 *  leads with what's *different* about this route vs the others on screen. */
function buildHeadline(route: ClassifiedRoute, peers: PeerRoute[] = []): string {
  const f: Partial<FeatureSummary> = route.features ?? {};
  const isDirect = route.classification === "fastest";

  // When a "pleasant"/"scenic" pick collapsed onto the direct route, describe
  // what it IS (the most direct way) — never what it lacks (Morgan: say what's
  // on the route, not what isn't).
  if (route.sameAsFastest && !isDirect) {
    return "The fastest way is also the pleasant one here";
  }

  const water = f.waterNames?.[0];
  const park = f.parkNames?.[0] ?? f.natureNames?.[0];
  const parkCount = (f.parkNames?.length ?? 0) + (f.natureNames?.length ?? 0);
  const historic = f.historicNames?.[0];
  const landmark = f.landmarkNames?.[0];
  const epic = route.route.epicHighwayLabels?.[0];
  const epicM = route.route.epicHighwayMeters ?? 0;
  const resid = f.residentialPct ?? 0;
  const hwy = f.highwayPct ?? 0;
  const major = f.majorRoadPct ?? 0;
  const treeKm = f.treeLinedKm ?? 0;

  // ---- Rung 1: named feature(s) — the most specific, most compelling, and
  // inherently distinguishing (a named river is rarely on two routes). ----
  // Only call out an epic highway when it's a MEANINGFUL chunk of the trip
  // (≥25% of the distance), not a 1 km sliver at the very end — otherwise a
  // cross-country route claims "Runs along PCH" for the last mile.
  if (epic && epicM > 2000 && epicM >= (route.route.distance ?? 0) * 0.25) {
    return `Runs along ${epic}`;
  }
  if (water && park) return `Along ${withArticle(water)}, past ${park}`;
  if (water) return resid >= 45 ? `Along ${withArticle(water)} on quiet streets` : `Along ${withArticle(water)}`;
  if (park && parkCount === 1) return `Through ${park}`;
  if (park && parkCount > 1) return `Past ${ordinalWord(parkCount)} parks${water ? " and water" : ""}`;
  if (historic) return `Through the ${historic} historic district`;
  if (landmark) return `Past ${landmark}`;

  // ---- Rung 1b: OUR OWN IMPORTED LAYER, when Overpass never answered. ----
  //
  // Every name above comes from the live Overpass FeatureSummary, so the whole
  // of rung 1 goes silent whenever the tile path fails — 84% of searches over
  // the last three days, and permanently outside the nine states the private
  // box holds. Dallas -> Austin fell straight past all six checks to a road
  // name, while scenic_coverage_for_path had 1,061 features and 26 nameable
  // places for that corridor, in 11 ms.
  //
  // buildRouteReason already builds exactly the right sentence from the layer
  // a few hundred lines below — "open farmland and orchards, through Parco
  // Regionale del Monte Subasio" — and the headline simply never asked for it.
  // Same call, same bars: greenPhrase returns null on an UNMEASURED route so an
  // unimported region still says nothing, and every naming bar (2 km beside or
  // inside before along/through, distance for landmarks, nothing unnamed) is
  // enforced in the database rather than here.
  //
  // Placed BELOW the Overpass names deliberately. Where both exist the live
  // pass has the specific water/park pairing this rung was written for; the
  // layer is what speaks when it did not run.
  const layerHeadline = sceneryClause(
    greenPhrase({
      summary: f.scenicLayer?.summary ?? null,
      coveredFraction: f.scenicLayer?.coveredFraction ?? 0,
    }),
    namedPhrase(f.scenicLayer?.named),
  );
  if (layerHeadline) {
    return layerHeadline.charAt(0).toUpperCase() + layerHeadline.slice(1);
  }

  // NOTE: curated 'great road' naming now happens in buildRouteReason, which
  // appends "… featuring <road>" to this base summary and surfaces the road's
  // blurb as a separate, expandable description on the card. Keeping it OUT of
  // the base headline means the summary stays the summary ("Less time on the
  // highway") and the road is added on top — see RouteReason.featured.

  // ---- Rung 2: comparative — what sets this route apart from its peers. ----
  const cmp = comparativeDifference(route, peers);
  if (cmp) return cmp;

  // ---- Rung 3: absolute road character (when there's nothing to compare). --
  if (treeKm >= 1.2 && resid >= 40) return "Tree-lined, mostly residential streets";
  if (resid >= 60) return "Quiet residential streets nearly the whole way";
  if (resid >= 40 && hwy < 15) return "Mostly calm local streets";

  // The single road this route is dominated by — names the concrete, honest
  // difference between two otherwise-similar big-road routes ("Mostly on
  // I-684" vs "Mostly on the Taconic") instead of a vague "mostly highway".
  // BUT only when it actually differentiates: if another surfaced route is on
  // that SAME road just as much, naming it tells the user nothing (the "both
  // routes are on Old Sleepy Hollow Rd" problem), so we drop it.
  let road = dominantRoadLabel(route);
  if (
    road &&
    peers.some((p) => p.dominantRoad && p.dominantRoad.toLowerCase() === road!.toLowerCase())
  ) {
    road = null;
  }

  // ---- Direct-route framing ----
  if (isDirect) {
    if (hwy >= 45) return road ? `Fastest — via ${road}` : "Fastest — mostly main roads";
    if (major >= 35) return road ? `Fastest — via ${road}` : "Fastest — along the main roads";
    return "The fastest way";
  }

  // ---- Rung 4: the floor — honest, never claims a false speed. ----
  // Same constant the SCENIC HONESTY gate uses — see MOSTLY_MAIN_ROADS_HWY_PCT.
  if (hwy >= MOSTLY_MAIN_ROADS_HWY_PCT) return road ? `Mostly on ${road}` : "Mostly on main roads";
  if (resid >= 25) return "Calmer local streets";
  if (road) return `Mostly on ${road}`;

  // ---- SCENIC HONESTY FLOOR (card-copy audit, Aug 24; Morgan's wording). ----
  //
  // Placed HERE, at the very bottom, and not higher up. My first attempt put
  // it above the comparative rung and it swallowed "Calmer local streets" — a
  // measured road fact that is perfectly honest and more useful than saying
  // nothing. A regression caught it. The rule is not "a Scenic card with no
  // scenery may say nothing"; it is "a Scenic card may not offer a GENERIC
  // line as though it were a reason".
  //
  // So every real fact above still wins. Only the last-resort filler is
  // replaced — which is exactly the line Phoenix → Prescott shipped ("A mix of
  // local and main roads") against +1h 51m and 79 extra miles.
  //
  // No quality claim, no invented scenery, and deliberately no attempt to make
  // the detour sound reasonable: whether it IS reasonable is a selection
  // question, and prose must not paper over it.
  //
  // WHAT WE MEASURED IS NOT THE DRIVER'S BUSINESS (Morgan, Aug 31: "the copy
  // should never tell us if scenery is measured or not — that is not for
  // public eyes"). This used to lead with "Scenery not measured — ", which
  // reports our own coverage state to someone who wants to know about a road.
  // Same class as the community score that leaked into Why This Route on
  // Aug 30: internal, and useless to them even when true.
  //
  // The gate itself stays. Its job was never the words "not measured" — it was
  // to stop a Scenic card offering a GENERIC line as though it were a reason.
  // So we still refuse to invent scenery, and we still say something concrete
  // when we have it: the distance and the road actually driven, which are
  // measured facts that need no caveat. When there is neither, we fall through
  // to the same generic line every other card can end on, because a vague line
  // is a smaller sin than telling the user about our data pipeline.
  if (route.classification === "scenic" && !sceneryMeasured(route)) {
    const miles = (route.route.distance ?? 0) / 1609.344;
    const dist = miles >= 1 ? `${miles.toFixed(miles >= 100 ? 0 : 1)} mi` : null;
    // `road` is null here by definition (checked above), so fall back to the
    // longest-driven named road. dominantRoadLabel needs 35% of the trip; a
    // sprawling detour like Phoenix → Prescott has no single road that big,
    // yet "via Wickenburg-Prescott Highway" is still the most useful anchor
    // we can honestly give.
    const longest = longestRoadName(route);
    if (longest && dist) return `${dist} via ${longest}`;
    if (dist) return `${dist} the long way round`;
  }

  return "A mix of local and main roads";
}

/** Join a short list naturally: ["A"]→"A", ["A","B"]→"A and B",
 *  ["A","B","C"]→"A, B, and C". */
function joinAnd(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

/** A WHOLE-TRIP description for long drives (Morgan: "talk about the whole
 *  length of the trip with maybe one or two magic moments" — not one niche
 *  road at the very start or end). Leads with the trip's honest overall
 *  character, then names up to two real highlights drawn from across the route,
 *  and notes how many pleasant roads it touches. The roads themselves stay in
 *  the card's expandable list. */
function buildJourneySummary(
  route: ClassifiedRoute,
  ranked: ReturnType<typeof curatedRanked>,
): string {
  const f: Partial<FeatureSummary> = route.features ?? {};
  const hwy = f.highwayPct ?? 0;
  const major = f.majorRoadPct ?? 0;
  const resid = f.residentialPct ?? 0;

  // Region / crossing clause (Morgan: "specific call outs to the region", "if we
  // cross state lines, I want to know that too", and "indicate when you cross
  // into different countries"). Derived from the route's own polyline.
  //   • International trips name the COUNTRIES crossed ("from France into
  //     Belgium and Germany") — works anywhere in Europe / the world.
  //   • A domestic US trip names the STATES crossed (richer than just "the
  //     United States"); other single-country trips skip the sub-region.
  const coords = route.route.coordinates ?? [];
  const crossing = (names: string[]): string =>
    names.length === 2
      ? `from ${names[0]} into ${names[1]}`
      : names.length >= 3
        ? `from ${names[0]} through ${joinAnd(names.slice(1, -1))} into ${names[names.length - 1]}`
        : "";

  const countries = countriesAlongRoute(coords);
  let region = "";
  let place = "";
  if (countries.length >= 2) {
    region = crossing(countries.map(displayCountry));
  } else if (countries[0] === "United States of America") {
    const states = statesAlongRoute(coords);
    region = crossing(states);
    if (!region && states.length === 1) place = ` across ${states[0]}`;
  }

  // The honest lead for a drive this long, from its overall road character —
  // folding in the region/crossings when we have them.
  let lead: string;
  if (region) {
    lead =
      hwy >= 55
        ? `Mostly main roads, ${region}`
        : resid >= 40
          ? `Calm country roads for much of the way, ${region}`
          : major >= 40
            ? `Mostly main roads, ${region}`
            : `Back roads wherever it can, ${region}`;
  } else if (hwy >= 55) {
    lead = `Mostly main roads for a trip this long${place}, with a few calmer stretches mixed in`;
  } else if (resid >= 40) {
    lead = `Stays on calm country roads for much of this long drive${place}`;
  } else if (major >= 40) {
    lead = `Follows the main roads for this long drive${place}, slipping onto quieter ones where it can`;
  } else {
    lead = `Trades main-road miles for back roads wherever it can${place} on a drive this long`;
  }

  // Up to two "magic moments" pulled from across the whole route.
  const moments: string[] = [];
  const park = f.parkNames?.[0] ?? f.natureNames?.[0];
  const water = f.waterNames?.[0];
  const historic = f.historicNames?.[0];
  if (park) moments.push(park);
  if (water && moments.length < 2) moments.push(withArticle(water));
  if (historic && moments.length < 2) moments.push(`the ${historic} historic district`);

  // No "N pleasant roads" count here — it read like the whole trip had just one
  // nice road. The roads are already named in the comparison line and the
  // expandable list; the journey summary stays about the whole-trip character.
  void ranked;
  const parts: string[] = [`${lead}.`];
  if (moments.length > 0) parts.push(`Look for ${joinAnd(moments)} along the way.`);
  return parts.join(" ");
}

/** The big roads a route actually drives — interstates, US/state highways,
 *  parkways, turnpikes, expressways — longest-first. Used for the Direct/Fast
 *  card's honest "here's the road you'll be on" line. Returns [] when none
 *  qualify (a short city hop with no named highway). */
function significantBigRoads(route: ClassifiedRoute, max = 3): string[] {
  const steps = (route.route.steps ?? []) as Array<{ roadName?: string; distance?: number; instruction?: string }>;
  const total = route.route.distance || 0;
  // Interstate / US / 2-letter state route / "Route N" / named limited-access.
  const BIG = /\b(?:I|US|U\.S\.)[-\s]?\d|\b[A-Z]{2}[-\s]?\d|\bRoute\s+\d|\b(?:Parkway|Pkwy|Turnpike|Tpke|Thruway|Expressway|Expwy|Freeway|Fwy|Motorway|Interstate)\b/i;
  // Some sources (Google's carrier route) carry only an `instruction` string —
  // "Continue on I-95 S", "Merge onto I-287 toward …" — and no roadName. Pull the
  // road out of the instruction so the Fast card can still list its big roads.
  const roadFromInstruction = (instr?: string): string => {
    if (!instr) return "";
    // Stop the road name at POI/landmark annotations ("Pass by Wendy's",
    // "(on the left in 0.7 mi)") and direction clauses — otherwise the Fast card
    // reads "Mostly via 3rd Ave Pass by Wendy's" (Morgan).
    const m = instr.match(
      /(?:\bon|\bonto|\bvia|\btake|\bmerge onto|\bstay on)\s+(.+?)(?:\s+toward\b|\s+for\b|\s+pass\b|\s+by\b|\s*\(|,|$)/i,
    );
    const cand = (m ? m[1] : instr).trim();
    return BIG.test(cand) ? cand : "";
  };
  const byRoad = new Map<string, { meters: number; name: string }>();
  for (const st of steps) {
    const name = (st.roadName ?? "").trim() || roadFromInstruction(st.instruction);
    if (!name || !BIG.test(name)) continue;
    const key = name.toLowerCase();
    const prev = byRoad.get(key) ?? { meters: 0, name };
    prev.meters += st.distance ?? 0;
    byRoad.set(key, prev);
  }
  const minMeters = Math.max(800, total * 0.06); // ≥ ~0.5 mi or ≥ 6% of the trip
  const ranked = [...byRoad.values()]
    .filter((r) => r.meters >= minMeters)
    .sort((a, b) => b.meters - a.meters)
    .map((r) => r.name);
  // Sanitize and de-duplicate AFTER ranking, then take `max` (audit, Aug 24).
  // The map above keys on the raw string, so one road under two dressings
  // survived as two entries and both reached the card: Boston → Portsmouth
  // read "Mostly via I-95 N, US-1 N Toll road, and I-95 N Toll road". Cleaning
  // before the slice means the card gets `max` DISTINCT roads rather than
  // `max` strings.
  return cleanRoadNameList(ranked, max);
}

/** Build the card's one-sentence reason + time trade.
 *  `peers` = the OTHER surfaced routes; pass them so the headline compares. */
export function buildRouteReason(
  route: ClassifiedRoute,
  fastestDurationS: number,
  peers: PeerRoute[] = [],
  /** The DIRECT route's polyline. When provided, the reason is built from where
   *  THIS route actually differs from Direct (the divergence) — naming the roads
   *  it veers onto and where — instead of roads it shares with Direct. */
  directCoords?: LngLat[],
): RouteReason {
  const isFastest = route.classification === "fastest" || !!route.sameAsFastest;

  // Hand-written reason for a flagship corridor (e.g. Boston → Maine coast)?
  // Takes priority over the generic builder when the endpoints match — but
  // lands on at most ONE card per trip (see signatureReason).
  const sig = signatureReason(route, isFastest, peers);
  if (sig) return sig;

  const base = buildHeadline(route, peers);

  // --- Where does this route actually LEAVE the direct route? (Morgan) ---
  // The shared stretch is, by definition, the same road Direct uses — so the
  // only thing worth explaining is the divergence. Compute it once.
  let divergence: { roads: string[]; position: string; coverFrac: number } | null = null;
  if (
    !isFastest &&
    directCoords &&
    directCoords.length >= 2 &&
    (route.route.coordinates?.length ?? 0) >= 2
  ) {
    const div = routeDivergence(route.route.coordinates, directCoords);
    if (div.spans.length > 0) {
      const biggest = div.spans.reduce((a, b) => (b.coverFrac > a.coverFrac ? b : a));
      divergence = {
        roads: roadsDuringDivergence(route.route.steps, route.route.distance || 0, div.spans),
        position: describeDivergencePosition(biggest.midFrac),
        coverFrac: div.totalDivergentFrac,
      };
    }
  }
  const divergentRoadSet = new Set((divergence?.roads ?? []).map((r) => r.toLowerCase()));

  // Curated 'great road' on this route → append "… featuring <road>" to the
  // base summary and surface the top road's blurb for the card's expandable
  // description. Never on the direct/collapsed-onto-direct route. We drop any
  // road the base already names, so we never get "Mostly on Round Hill Road,
  // featuring Round Hill Road".
  let headline = base;
  let featured: RouteReason["featured"];
  if (!isFastest) {
    const ranked = curatedRanked(route);
    const longTrip = (route.route.duration ?? 0) > 40 * 60;
    // A ROAD TRIP (>3h): the card should talk about the whole journey — regions
    // and states crossed, a magic moment or two — not a one-mile road by the
    // start. "People might want to go slow" — different standards apply.
    const roadTrip = (route.route.duration ?? 0) > 3 * 3600;
    const isScenic = route.classification === "scenic";
    // CHARMING-TOWNS road trip: this route was deliberately threaded through cute
    // towns (Morgan: "going through great towns"). Lead with the towns — that's
    // the whole reason to take this way — and let the journey summary fill in the
    // regions crossed. Takes priority over the curated-road / divergence story.
    const townNames = route.route.townNames ?? [];
    // THROUGH-THE-PARK walk: routed across a major park's internal paths. That
    // park IS the reason — lead with it. Highest priority for walks.
    const parkName = route.route.parkName;
    // NICE CITY STREETS variety walk: threaded through a sequence of curated
    // pretty blocks. Lead with the streets themselves — that IS the route's
    // reason. Names arrive as full segment labels ("Jane Street: Hudson Street
    // to Greenwich Avenue"); show just the street, deduped (adjacent blocks
    // often share one).
    const varietyStreets = route.route.varietyStreets ?? [];
    if (parkName) {
      headline = `Through ${parkName}`;
      featured = {
        name: parkName,
        blurb: `Crosses ${parkName} on its paths — real green space, not just streets.`,
        roads: [],
      };
    } else if (varietyStreets.length > 0) {
      const seenStreet = new Set<string>();
      const streets = varietyStreets
        // Strip the segment range/label: "Jane Street: Hudson St to Greenwich"
        // and "Via dei Fori Imperiali — segment 08" → just the street name.
        .map((s) => s.split(/[:—]/)[0].trim())
        .filter((n) => {
          const k = n.toLowerCase();
          if (!n || seenStreet.has(k)) return false;
          seenStreet.add(k);
          return true;
        });
      headline = `Through ${joinAnd(streets.slice(0, 3))}`;
      featured = {
        name: streets[0],
        blurb:
          streets.length > 1
            ? `A wander down some of the neighborhood's prettiest blocks — ${joinAnd(streets.slice(0, 4))}.`
            : `A detour onto ${streets[0]}, one of the neighborhood's prettiest blocks.`,
        roads: [],
      };
    } else if (townNames.length > 0) {
      headline = `Through ${joinAnd(townNames.slice(0, 3))}`;
      featured = {
        name: townNames[0],
        blurb: buildJourneySummary(route, ranked),
        roads: [],
      };
    } else if (ranked.length > 0) {
      // Dedupe by name (case-insensitive): two distinct segments often share a
      // name (e.g. "River Road" in adjacent towns), which otherwise reads as
      // "featuring River Road, River Road and 3 more".
      const seenRoad = new Set<string>();
      // Display form ("Ohio River Scenic Byway", not "Ohio River Scenic
      // Byway (east): Aurora / Vevay / Madison"). Deduping AFTER cleaning
      // also merges the (east)/(west) halves of one byway into one mention.
      const allRoads = ranked
        .map((r) => displayRoadName(r.name))
        .filter((n) => {
          const k = n.toLowerCase().trim();
          if (!n || seenRoad.has(k)) return false;
          seenRoad.add(k);
          return true;
        });
      const totalMeters = route.route.distance || 0;
      const topFraction = totalMeters > 0 ? ranked[0].meters / totalMeters : 1;
      // Is the top featured road a real chunk of THIS drive? Significant means
      // >=4% of the trip OR a substantial absolute stretch (>=10 mi). The
      // absolute floor keeps EPIC highways like the Beartooth (97 mi — only ~3%
      // of a cross-country trip, but very much the point) while dropping a
      // 1-mile country road that's a rounding error on a 1,200-mile drive.
      const SIGNIFICANT_M = 16000; // ~10 miles
      const featuredSignificant =
        topFraction >= 0.04 || (ranked[0].meters ?? 0) >= SIGNIFICANT_M;
      // Blurb (Morgan): on a ROAD TRIP the description should be about the WHOLE
      // journey — the regions/states crossed and a magic moment or two — not one
      // niche road. The Scenic card on a 1,200-mile drive should NOT be a story
      // about "Sleepy Hollow Road near Briarcliff Manor". Use the journey summary
      // when it's a long PLEASANT trip, OR a road trip whose featured road isn't
      // a significant stretch. Keep the single-road story when that road really
      // IS the drive (a short trip, or a significant road on a road trip).
      const useJourney =
        (longTrip && !isScenic) || (roadTrip && !featuredSignificant);
      const blurb = useJourney ? buildJourneySummary(route, ranked) : ranked[0].blurb;
      featured = { name: displayRoadName(ranked[0].name), blurb, roads: allRoads };
      // Length-aware headline: don't headline a niche road that's a tiny slice of
      // a long drive — "featuring Round Hill Road" on a 345-mile trip is absurd,
      // and "Sleepy Hollow Road" on a cross-country trip even more so. On a road
      // trip, only headline a road that's genuinely significant (even the Scenic
      // card). Below road-trip scale, keep the prior behavior.
      const featureInHeadline = roadTrip
        ? featuredSignificant
        : isScenic || !longTrip || topFraction >= 0.04;
      if (featureInHeadline) {
        const onDivergence = (n: string) => {
          if (divergentRoadSet.size === 0) return true; // no Direct route to compare against
          const nl = n.toLowerCase();
          return [...divergentRoadSet].some((d) => d.includes(nl) || nl.includes(d));
        };
        const names = allRoads
          .filter((n) => !base.toLowerCase().includes(n.toLowerCase()))
          // Don't headline a curated road that's SHARED with the direct route —
          // it explains nothing. Only name roads on the divergent stretch.
          .filter(onDivergence);
        if (names.length > 0) headline = `${base}, ${featuringSuffix(names)}`;
      }
    } else if ((longTrip && !isScenic) || roadTrip) {
      // A long PLEASANT trip OR any road trip with no curated road on it — still
      // describe the whole journey (regions/states crossed) rather than leaving
      // the card with no story. Includes the Scenic card on a road trip.
      featured = { name: "", blurb: buildJourneySummary(route, []), roads: [] };
    }
  }

  // The peer comparison — shown as a second line, but only when it adds
  // something the headline doesn't already say (the base summary often IS the
  // comparison, in which case we don't repeat it).
  const cmp = comparativeDifference(route, peers);

  // Tradeoff line. Pleasant routes hide the time penalty (the ETA tells it).
  // The SCENIC card is the opposite: the big extra time IS the offer — "the
  // long way" — so we frame it honestly and positively. This is the
  // super-scenic, hour-plus-no-limit option, not the everyday Pleasant pick.
  // (Morgan) NO time comparison on the cards — the per-route "+X more" deltas are
  // unreliable (a route's stored duration can be free-flow or a stale ETA, not the
  // traffic time the card shows), so the delta is "always wrong". We drop it
  // entirely rather than display a bad number. The cards still show each route's
  // absolute time + distance (so they can be compared directly) and the
  // qualitative road story. buildTradeoff retained for potential non-numeric use.
  // STILL DISABLED HERE, DELIBERATELY (card-copy audit, Aug 24).
  //
  // Morgan asked for the time trade to be stated, and buildTradeoff above has
  // been rewritten to do it. It is NOT re-enabled at this call site, because
  // the objection recorded above is real and I reproduced it: on
  // Asheville → Hendersonville the Pleasant card's own
  // `duration - fastestDuration` came to **+2 min**, while the Fast card
  // rendered on screen beside it was 1907s against this route's 2680s — a
  // **+13 min** difference. Two different baselines, one card.
  //
  // Re-enabling here would print whichever of those happened to be nearer to
  // hand, and a confidently wrong number is exactly the failure this audit is
  // about. The trade belongs where the DISPLAYED durations live — RouteCard,
  // which already holds `peers` and the eta-plan output — so the delta is
  // computed from the same numbers the driver is looking at.
  //
  // Flagged to Morgan as a blocking question rather than guessed at.
  void buildTradeoff;
  const tradeoff: string | undefined = undefined;

  // Bike-lane share (Morgan: "I want that to be part of the blurb too —
  // percentage of bike lanes"). On a BIKE route, lead the secondary line with
  // how much of the ride is on bike lanes — the number cyclists care about most.
  // Notes whether it's mostly protected when we know. Overrides the generic
  // comparison; falls back to it when there are no lanes.
  let comparison = cmp && cmp !== base && cmp !== headline ? cmp : undefined;
  if (routeMode(route) === "biking") {
    const km = (route.route.distance ?? 0) / 1000;
    const laneKm = route.features?.bikeLaneKm ?? 0;
    const pct = km > 0 ? Math.round((laneKm / km) * 100) : 0;
    if (pct >= 5) {
      const protectedKm = route.features?.protectedBikeKm ?? 0;
      const mostlyProtected = laneKm > 0 && protectedKm / laneKm >= 0.6;
      comparison = `${pct}% on bike lanes${mostlyProtected ? " — mostly protected" : ""}`;
    }
  } else if (
    !comparison &&
    route.classification === "pleasant" &&
    divergence &&
    divergence.coverFrac < 0.3 &&
    route.route.duration <= fastestDurationS * 1.25
  ) {
    // (Morgan) MOSTLY-THE-FAST-ROUTE trip. This route runs the fast route for
    // almost the whole way (coverFrac < 0.3 — it only leaves the fast line for a
    // short stretch), then ducks off it for some greenery. It's the most
    // reasonably pleasant option that's still basically the fast way. Say exactly
    // that: it leaves the fast route for a stretch to get some greenery in. The
    // card's time pill already shows the few extra minutes, so the line stays
    // about the road, not the clock. (It's NOT the most pleasant — the Scenic
    // card, longer, owns that.)
    // Don't repeat a road the headline already named ("featuring Sleepy Hollow
    // Road" up top + "…Sleepy Hollow Road midway through" below read as
    // repetitive). Only name roads the headline hasn't already mentioned.
    // cleanRoadNameList replaces the bare slice (audit, Aug 24). "Takes
    // Cabrillo Highway and Cabrillo Highway South" on SF → Santa Cruz was one
    // road (CA-1) filling both slots, so the line named nothing new. Cleaning
    // before the slice means two slots hold two DISTINCT roads.
    const named = cleanRoadNameList(
      divergence.roads.filter(
        (r) => /[a-z]/i.test(r) && !headline.toLowerCase().includes(r.toLowerCase()),
      ),
      2,
    );
    // Empty when the position is not worth claiming (see describeDivergencePosition).
    const greenWhere = divergence.position ? ` ${divergence.position}` : "";
    // Only claim "greenery" when the route ACTUALLY has some (parks, tree-lined
    // streets, or waterfront). The variety walk is a STREETS route — saying it
    // "leaves the fast route for greenery" when there's none is just wrong
    // (Morgan). When there's no green, describe it honestly as quieter streets.
    const f = route.features;
    // WHAT KIND OF GREEN, when our own layer has looked at this drive.
    //
    // "some greenery" was the only phrase available, because `f.parks` folds
    // farmland, meadow, orchard, vineyard, forest and parks into one number.
    // Measured Aug 17 on our layer, these two drives were indistinguishable to
    // this line and are not remotely the same road:
    //   Perugia-Foligno   97 fields · 110 orchards · 75 woods
    //   Taconic Parkway   218 woods · 148 water · 28 fields
    // greenPhrase returns null when the route is UNMEASURED, so an unimported
    // region falls back to the old wording rather than claiming an absence.
    const layerGreen = greenPhrase({
      summary: f?.scenicLayer?.summary ?? null,
      coveredFraction: f?.scenicLayer?.coveredFraction ?? 0,
    });
    // AND THE THING YOU WILL ACTUALLY SEE. The kind of green says what sort of
    // drive it is; the name says which drive it is. Morgan, Aug 17: "both,
    // name second".
    //
    //   "open farmland and orchards, through Parco Regionale del Monte Subasio"
    //
    // Every bar behind a name is enforced in the database, not here: two
    // kilometres beside or inside a thing before along/through may be said,
    // distance for landmarks, no streams, no canals, and nothing unnamed. This
    // line only decides where the words go. The Fiume Chiascio manages 924m on
    // the Perugia-Foligno road and is therefore NOT named, which is the bar
    // doing its job on the first road it was tried on.
    const layerNamed = namedPhrase(f?.scenicLayer?.named);
    const layerScenery = sceneryClause(layerGreen, layerNamed);
    const hasGreen =
      layerScenery !== null ||
      (f?.parks ?? 0) > 0 ||
      (f?.treeLinedKm ?? 0) > 0.1 ||
      (f?.waterPct ?? 0) > 5;
    // Name it when we can, fall back to the old generic when we cannot. An
    // UNMEASURED region yields null from both halves and lands on the wording
    // this line used before the layer existed - never on a claim of absence.
    const greenWords = layerScenery ?? "some greenery";
    if (named.length) {
      comparison = `Mostly the fast route — leaves it${greenWhere} for ${joinAnd(named)}${hasGreen ? ` and ${greenWords}` : ""}`;
    } else {
      comparison = `Mostly the fast route — leaves it${greenWhere} for ${hasGreen ? greenWords : "quieter streets"}`;
    }
  } else if (!comparison && divergence) {
    // FALLBACK ONLY. When we have a genuine pleasantness reason (cmp — "Less
    // time on main roads", "Passes more parks", "More tree-lined streets") we
    // LEAD with it: that's the real answer to "why take this over Direct".
    // Only when there's no such reason do we fall back to naming WHERE it
    // leaves the direct line and onto what — honest, but not a benefit claim.
    // Don't repeat a road the headline already named ("featuring Sleepy Hollow
    // Road" up top + "…Sleepy Hollow Road midway through" below read as
    // repetitive). Only name roads the headline hasn't already mentioned.
    // cleanRoadNameList replaces the bare slice (audit, Aug 24). "Takes
    // Cabrillo Highway and Cabrillo Highway South" on SF → Santa Cruz was one
    // road (CA-1) filling both slots, so the line named nothing new. Cleaning
    // before the slice means two slots hold two DISTINCT roads.
    const named = cleanRoadNameList(
      divergence.roads.filter(
        (r) => /[a-z]/i.test(r) && !headline.toLowerCase().includes(r.toLowerCase()),
      ),
      2,
    );
    // Lead with what it TAKES (the nicer roads), not what it "leaves" — the old
    // "Leaves the direct route in the middle for Polpis Road" read backwards
    // (Morgan). Frame the detour as the choice it is.
    const where = divergence.position ? ` ${divergence.position}` : "";
    comparison = named.length
      ? `Takes ${joinAnd(named)}${where}`
      : `Swings onto quieter roads${where}`;
  }

  // The Direct/Fast card is honest about the trade: name the big roads it puts
  // you on (highways, parkways, the busy state routes) — "here's the road
  // you'll be on", the bad parts and all. Overrides the generic line above.
  if (isFastest) {
    const big = significantBigRoads(route);
    if (big.length) comparison = `Mostly via ${joinAnd(big)}`;
  }

  // Positive fallback (Morgan): a Pleasant card must never read bare — even
  // when this route happens to ALSO be the quickest way and so produced no
  // divergence-based "why". Name what's genuinely good ON the route (the park
  // it threads, the water it follows, the landmark it passes) so the card
  // always says what's good rather than nothing. Never applied to the plain
  // Direct/fast card (handled above) — that one stays honest about big roads.
  if (!comparison && !isFastest) {
    const f = route.features;
    const water = f?.waterNames?.[0];
    const park = f?.parkNames?.[0] ?? f?.natureNames?.[0];
    const landmark = f?.landmarkNames?.[0] ?? f?.historicNames?.[0];
    const town = niceTownsAlongRoute(route.route.coordinates ?? [])[0]?.name;
    if (water) comparison = `Along ${water}`;
    else if (park) comparison = `Through ${park}`;
    else if (town) comparison = `Through ${town}`;
    else if (landmark) comparison = `Past ${landmark}`;
  }

  return {
    headline,
    comparison,
    tradeoff,
    featured,
  };
}

// ---------------------------------------------------------------------------
// buildRouteIntroNarration — the SPOKEN welcome at the start of a drive.
//
// This is the app's thesis made audible: at the moment navigation begins,
// a warm voice explains why this route was chosen and what it trades. It is
// the single most "blown away" moment in the product — the sentence that
// makes a driver smile before the first turn. Phrased for the ear (warmer,
// fuller) than the terse card line, and it stays SILENT for a plain direct
// route, because directness needs no justification.
//
// Returns null when there's nothing worth saying (direct route, or a
// pleasant pick that collapsed onto direct) — the caller speaks nothing.
// ---------------------------------------------------------------------------

/** Spoken time-trade clause, phrased for the ear. The same-time case is the
 *  delight: a nicer road at no cost is the best thing the app can say. */
function spokenTrade(deltaMin: number): string {
  if (deltaMin <= 1) return "and it doesn't cost you any extra time";
  if (deltaMin <= 3) return "just a few minutes slower than the quickest way, and worth it";
  return `about ${deltaMin} minutes slower than the quickest way, but worth it`;
}

export function buildRouteIntroNarration(
  route: ClassifiedRoute,
  fastestDurationS: number,
): string | null {
  // The direct route — and any pleasant pick that collapsed onto it — gets
  // no spoken justification. Silence is the honest choice there.
  if (route.classification === "fastest" || route.sameAsFastest) return null;

  const f: Partial<FeatureSummary> = route.features ?? {};
  const water = f.waterNames?.[0];
  const park = f.parkNames?.[0] ?? f.natureNames?.[0];
  const historic = f.historicNames?.[0];
  const epic = route.route.epicHighwayLabels?.[0];
  const epicM = route.route.epicHighwayMeters ?? 0;
  const resid = f.residentialPct ?? 0;

  const deltaMin = Math.round((route.route.duration - fastestDurationS) / 60);
  const trade = spokenTrade(deltaMin);

  // Lead clause — the named feature is what makes this land. Falls back to
  // road character so the intro still fires on an ordinary pretty drive.
  let lead: string;
  if (epic && epicM > 1000) lead = `Taking you along ${epic}`;
  else if (water && park) lead = `Taking you along ${withArticle(water)}, past ${park}`;
  else if (water) lead = `Taking the road along ${withArticle(water)}`;
  else if (park) lead = `Routing you past ${park}`;
  else if (historic) lead = `Taking you through the ${historic} historic district`;
  else if (resid >= 45) lead = "Taking the quieter streets";
  else return null; // nothing distinctive to say — don't manufacture awe

  return `${lead} — ${trade}.`;
}
